import React from 'react';
import { Smartphone, Zap, Gift, Plug, Cloud, Shield, Monitor, BarChart3, Globe } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ProductsRedesign = () => {
  const appFeatures = [
    {
      icon: Monitor,
      title: "Real-time insights",
      description: "Know exactly when and how your home is using power",
      color: "bg-blue-100 text-blue-600"
    },
    {
      icon: Gift,
      title: "Earned it? Track it.",
      description: "Watch your energy coins stack up as you use clean electricity",
      color: "bg-green-100 text-green-600"
    },
    {
      icon: BarChart3,
      title: "Use your rewards",
      description: "Redeem Wenergie Coins in the app's built-in rewards store",
      color: "bg-purple-100 text-purple-600"
    },
    {
      icon: Smartphone,
      title: "One app for everything",
      description: "Works across homes, solar systems, and electric vehicles",
      color: "bg-orange-100 text-orange-600"
    }
  ];

  const rederBenefits = [
    {
      icon: Plug,
      title: "Plug-and-play hardware",
      description: "Setup takes just minutes"
    },
    {
      icon: Zap,
      title: "Instant eligibility",
      description: "Start earning rewards from day one"
    },
    {
      icon: Cloud,
      title: "Full compatibility",
      description: "Tailored for systems that need extra support connecting"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar/>
      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-sm uppercase tracking-widest text-gray-500 font-medium mb-4">Our Products</h1>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Power Your Clean Energy Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From smart monitoring to seamless solar integration, our products make clean energy accessible, trackable, and rewarding.
            </p>
          </div>
        </div>
      </section>

      {/* ReDER Section - NOW FIRST */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {/* ReDER Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                <Plug className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Meet ReDER — Your Gateway to Clean Energy
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Smart hardware that connects your home to the Wenergie Network
              </p>
            </div>

            {/* ReDER Products */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* ReDER Personal Energy Assistant */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden group hover:shadow-xl transition-shadow duration-300 flex flex-col">
                <div className="relative">
                  <img
                    src="/assets/product.png"
                    alt="ReDER Personal Energy Assistant"
                    className="w-full h-68 object-cover"
                  />
                   
                </div>
                <div className="p-8 flex-grow flex flex-col">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    ReDER Personal Energy Assistant
                  </h3>
                  <p className="text-lg text-gray-700 mb-4">
                    Powerful energy insights. Simple setup.
                  </p>
                  <p className="text-gray-600 mb-8">
                    ReDER is your home's connection to the Wenergie Network. It tracks your energy use in real-time and connects to the cloud via WiFi, unlocking access to clean power and rewards.
                  </p>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                      <Shield className="h-5 w-5 text-gray-700 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-gray-900">ReDER for Single-Phase Homes</h4>
                        <p className="text-gray-600">Standard homes, simplified tracking</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                      <Globe className="h-5 w-5 text-gray-700 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-gray-900">ReDER for Three-Phase Homes</h4>
                        <p className="text-gray-600">Optimized for higher loads and larger homes</p>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-6">
                    Plug it in. Connect to WiFi. Start earning.
                  </p>

                  {/* Pushed to bottom with mt-auto */}
                  <div className="mt-auto">
                    <a
                      href="https://rzp.io/rzp/7NYFHr5c"
                      target="_blank"
                      className="block w-full py-3 bg-blue-600 text-white text-center rounded-lg font-medium hover:bg-blue-700 transition-colors duration-300"
                    >
                      Buy ReDER Personal Energy Assistant
                    </a>
                  </div>
                </div>
              </div>

              {/* ReDER Companion */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden group hover:shadow-xl transition-shadow duration-300 flex flex-col">
                <div className="relative">
                  <img
                    src="/assets/product.png"
                    alt="ReDER Companion"
                    className="w-full h-68 object-cover"
                  />
                   
                </div>
                <div className="p-8 flex-grow flex flex-col">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    ReDER Companion
                  </h3>
                  <p className="text-lg text-gray-700 mb-4">
                    Make your solar smart — and rewarding.
                  </p>
                  <p className="text-gray-600 mb-8">
                    For homes with Delta, Solivia, or Enphase solar systems, ReDER Companion bridges the gap. It connects your inverter to the Wenergie Network via WiFi — no technician needed.
                  </p>

                  <div className="space-y-6 mb-8">
                    {rederBenefits.map((benefit, index) => (
                      <div key={index} className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <benefit.icon className="h-5 w-5 text-gray-700" />
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{benefit.title}</h4>
                          <p className="text-gray-600">{benefit.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="text-gray-700 mb-6">
                    Compatible with major solar brands
                  </p>

                  {/* Pushed to bottom with mt-auto */}
                  <div className="mt-auto">
                    <a
                      href="https://rzp.io/rzp/7NYFHr5c"
                      target="_blank"
                      className="block w-full py-3 bg-green-600 text-white text-center rounded-lg font-medium hover:bg-green-700 transition-colors duration-300"
                    >
                      Buy ReDER Companion
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Section - NOW SECOND */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {/* App Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
                <Smartphone className="h-10 w-10 text-blue-600" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                The Wenergie App
              </h2>
              <p className="text-xl text-gray-700 mb-4">
                Your energy, at your fingertips
              </p>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
                Track your real-time electricity consumption, see when clean energy is flowing, and monitor your rewards — all from one clean, intuitive interface.
              </p>

              {/* App Image Placeholder */}
              <div className="max-w-md mx-auto mb-12 rounded-xl overflow-hidden shadow-lg">
                <img
                  src="/api/placeholder/540/960"
                  alt="Wenergie App Interface"
                  className="w-full"
                />
              </div>
            </div>

            {/* App Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {appFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="group p-6 rounded-xl bg-white hover:bg-white hover:shadow-lg transition-all duration-300 border border-gray-100"
                >
                  <div className={`w-12 h-12 rounded-lg ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>

            {/* App CTA */}
            <div className="text-center mt-12">
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="https://rzp.io/rzp/7NYFHr5c"
                  target="_blank"
                  className="flex items-center gap-2 px-6 py-3 bg-black text-white rounded-full hover:bg-gray-800 transition-colors duration-300"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17 3a3 3 0 0 1 2.995 2.824L20 6v12a3 3 0 0 1-2.824 2.995L17 21H7a3 3 0 0 1-2.995-2.824L4 18V6a3 3 0 0 1 2.824-2.995L7 3h10zm0 2H7a1 1 0 0 0-.993.883L6 6v12a1 1 0 0 0 .883.993L7 19h10a1 1 0 0 0 .993-.883L18 18V6a1 1 0 0 0-.883-.993L17 5zm-5 10a1 1 0 0 1 .117 1.993L12 17h-.01a1 1 0 0 1-.117-1.993L12 15h.01z" />
                  </svg>
                  Download for iOS
                </a>
                <a
                  href="https://rzp.io/rzp/7NYFHr5c"
                  target="_blank"
                  className="flex items-center gap-2 px-6 py-3 bg-black text-white rounded-full hover:bg-gray-800 transition-colors duration-300"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                  Download for Android
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Ready to start your clean energy journey?</h2>
            <p className="text-xl text-gray-600 mb-8">Join thousands of households already saving energy and earning rewards with Wenergie.</p>
            <a
              href="https://rzp.io/rzp/7NYFHr5c"
              target="_blank"
              className="inline-block px-8 py-4 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-all duration-300 transform hover:scale-105"
            >
              Get Started Today
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ProductsRedesign;